// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import Form from 'antd/lib/form';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';

import CVATSigningInput, { CVATInputType } from 'components/signing-common/cvat-signing-input';
import { CombinedState } from 'reducers';
import { useAuthQuery, usePlugins } from 'utils/hooks';
import GoogleAuthButton from 'components/google-auth-button';

export interface LoginData {
    credential: string;
    password: string;
}

interface Props {
    renderResetPassword: boolean;
    renderRegistrationComponent: boolean;
    renderBasicLoginComponent: boolean;
    fetching: boolean;
    onSubmit(loginData: LoginData): void;
}

function LoginFormComponent(props: Props): JSX.Element {
    const {
        fetching, onSubmit, renderResetPassword, renderRegistrationComponent, renderBasicLoginComponent,
    } = props;

    const authQuery = useAuthQuery();
    const [form] = Form.useForm();
    const [credential, setCredential] = useState('');
    const pluginsToRender = usePlugins(
        (state: CombinedState) => state.plugins.components.loginPage.loginForm,
        props,
        { credential },
    );

    let resetSearch = authQuery ? new URLSearchParams(authQuery).toString() : '';
    if (credential.includes('@')) {
        const updatedAuthQuery = authQuery ? { ...authQuery, email: credential } : { email: credential };
        resetSearch = new URLSearchParams(updatedAuthQuery).toString();
    }

    const registrationQuery = authQuery ? new URLSearchParams(authQuery).toString() : '';

    return (
        <div className='cvat-login-form-wrapper'>
            <Title level={3} className='cvat-signing-card-title'>Sign in</Title>
            <Form
                className='cvat-login-form'
                form={form}
                onFinish={(loginData: LoginData) => {
                    onSubmit(loginData);
                }}
            >
                {renderBasicLoginComponent && (
                    <>
                        <Form.Item
                            className='cvat-credentials-form-item'
                            name='credential'
                            rules={[{ required: true, message: 'Please specify an email or username' }]}
                        >
                            <Input
                                id='credential'
                                autoComplete='username'
                                placeholder='Email or username'
                                allowClear
                                onChange={(event) => setCredential(event.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            className='cvat-credentials-form-item'
                            name='password'
                            rules={[{ required: true, message: 'Please specify a password' }]}
                        >
                            <CVATSigningInput
                                type={CVATInputType.PASSWORD}
                                id='password'
                                placeholder='Password'
                                autoComplete='current-password'
                            />
                        </Form.Item>
                        {renderResetPassword && (
                            <div className='cvat-credentials-link cvat-forgot-password-link'>
                                <Link to={{ pathname: '/auth/password/reset', search: resetSearch }}>
                                    Forgot password?
                                </Link>
                            </div>
                        )}
                        <Form.Item>
                            <Button
                                type='primary'
                                className='cvat-credentials-action-button'
                                loading={fetching}
                                htmlType='submit'
                            >
                                Sign in
                            </Button>
                        </Form.Item>
                        <div className='cvat-signing-separator'>
                            <span>or</span>
                        </div>
                    </>
                )}
                {
                    pluginsToRender.map(({ component: Component }, index) => (
                        <Component targetProps={props} targetState={{ credential }} key={index} />
                    ))
                }
                <GoogleAuthButton isLogin />
            </Form>
            {renderRegistrationComponent && (
                <div className='cvat-signing-footer'>
                    <Text type='secondary'>New user?&nbsp;</Text>
                    <Link to={{ pathname: '/auth/register', search: registrationQuery }}>
                        Create an account
                    </Link>
                </div>
            )}
        </div>
    );
}

export default React.memo(LoginFormComponent);
